import * as d3 from "d3"
import { ButtonSelection } from "./types"
import * as params from "./params"
import { DragDirections } from "./resizers"

export const collectSiblingEdges = (
  snapButtons: ButtonSelection,
): { left: number[]; right: number[]; top: number[]; bottom: number[] } => {
  const siblingEdges = snapButtons
    .data()
    .filter(
      (datum) =>
        !Number.isNaN(datum.x) &&
        !Number.isNaN(datum.y) &&
        !Number.isNaN(datum.width) &&
        !Number.isNaN(datum.height),
    )
    .map((datum) => ({
      left: datum.x,
      right: datum.x + datum.width,
      top: datum.y,
      bottom: datum.y + datum.height,
    }))

  return {
    left: Array.from(
      new Set(
        siblingEdges.reduce(
          (accumulator: number[], siblingEdge) => [...accumulator, siblingEdge.left],
          [],
        ),
      ),
    ).sort((a, b) => a - b),
    right: Array.from(
      new Set(
        siblingEdges.reduce(
          (accumulator: number[], siblingEdge) => [...accumulator, siblingEdge.right],
          [],
        ),
      ),
    ).sort((a, b) => a - b),
    top: Array.from(
      new Set(
        siblingEdges.reduce(
          (accumulator: number[], siblingEdge) => [...accumulator, siblingEdge.top],
          [],
        ),
      ),
    ).sort((a, b) => a - b),
    bottom: Array.from(
      new Set(
        siblingEdges.reduce(
          (accumulator: number[], siblingEdge) => [...accumulator, siblingEdge.bottom],
          [],
        ),
      ),
    ).sort((a, b) => a - b),
  }
}

type SnapInfoInDirection = {
  // an array of numbers containing the coordinates to be snapped to,
  // deduplicated and in ascending order
  snapPoints: number[]
  // the largest index such that snapPoints[currentIndex] <= the current coordinate
  currentIndex: number
  // the difference between the current coordinate and either snapPoints[currentIndex] or snapPoints[currentIndex + 1],
  // whichever has smaller absolute value
  currentOffset: number
  // a d3 selection containing the line drawn as a visual aid for the user
  indicator: d3.Selection<SVGLineElement, unknown, HTMLElement, any>
}

export type EdgesSnapInfo = {
  left: SnapInfoInDirection
  right: SnapInfoInDirection
  top: SnapInfoInDirection
  bottom: SnapInfoInDirection
}

const valueAt =
  (snapInfo: SnapInfoInDirection, max: number) =>
  (index: number): number => {
    if (index < 0) {
      return 0
    }
    if (index >= snapInfo.snapPoints.length) {
      return max
    }
    return snapInfo.snapPoints[index]
  }

const updateSnapInfo = (snapInfo: SnapInfoInDirection, coordinate: number, max: number) => {
  // we want to find the index in [-1, snapInfo.snapPoints.horizontal.length] such that
  // valueAt(index) < coordinate < valueAt(index + 1)
  // most of the time, the current index will be correct, so we only change it if necessary
  // and start from the current index for efficiency
  let index = snapInfo.currentIndex
  while (index > -1 && valueAt(snapInfo, max)(index) > coordinate) {
    index--
  }
  while (index + 1 < snapInfo.snapPoints.length && valueAt(snapInfo, max)(index + 1) < coordinate) {
    index++
  }
  if (snapInfo.currentIndex !== index) {
    snapInfo.currentIndex = index
  }

  // the lowest and highest indices represent edges
  // don't snap to these
  if (snapInfo.currentIndex === -1) {
    snapInfo.currentOffset = valueAt(snapInfo, max)(0) - coordinate
    return
  }
  // the larger of the two options is at index + 1
  // this is out of bounds if it is larger than length(array) - 1
  if (snapInfo.currentIndex + 1 > snapInfo.snapPoints.length - 1) {
    // in which case we should choose the smaller option (the largest actual snap point, excluding edges)
    snapInfo.currentOffset = valueAt(snapInfo, max)(snapInfo.snapPoints.length - 1) - coordinate
    return
  }

  snapInfo.currentOffset = minByAbsoluteValue(
    valueAt(snapInfo, max)(snapInfo.currentIndex) - coordinate,
    valueAt(snapInfo, max)(snapInfo.currentIndex + 1) - coordinate,
  )
}

const minByAbsoluteValue = (a: number, b: number) => {
  return Math.abs(a) < Math.abs(b) ? a : b
}

export const init = (snapButtons: ButtonSelection): EdgesSnapInfo => {
  const createIndicator = (horizontal: boolean) => {
    return d3
      .select("#svg-dashboard")
      .append("line")
      .classed("alignment-indicator", true)
      .attr("x1", 0)
      .attr("x2", horizontal ? params.width : 0)
      .attr("y1", 0)
      .attr("y2", horizontal ? 0 : params.height)
      .attr("stroke", "#cb7d00")
      .attr("opacity", 0)
  }

  const siblingEdges = collectSiblingEdges(snapButtons)
  return {
    left: {
      snapPoints: siblingEdges.left,
      currentIndex: 0,
      currentOffset: 0,
      indicator: createIndicator(false),
    },
    right: {
      snapPoints: siblingEdges.right,
      currentIndex: 0,
      currentOffset: 0,
      indicator: createIndicator(false),
    },
    top: {
      snapPoints: siblingEdges.top,
      currentIndex: 0,
      currentOffset: 0,
      indicator: createIndicator(true),
    },
    bottom: {
      snapPoints: siblingEdges.bottom,
      currentIndex: 0,
      currentOffset: 0,
      indicator: createIndicator(true),
    },
  }
}

export const update = (
  snapInfo: EdgesSnapInfo,
  unsnappedPosition: { x: number; y: number; width: number; height: number },
  dragDirections: DragDirections = {
    top: true,
    right: true,
    bottom: true,
    left: true,
  },
): { x: number; y: number; width: number; height: number } => {
  const unsnappedDimensions = {
    top: unsnappedPosition.y,
    right: unsnappedPosition.x + unsnappedPosition.width,
    bottom: unsnappedPosition.y + unsnappedPosition.height,
    left: unsnappedPosition.x,
  }

  if (dragDirections.top) {
    updateSnapInfo(snapInfo.top, unsnappedDimensions.top, params.height)
  }
  if (dragDirections.right) {
    updateSnapInfo(snapInfo.right, unsnappedDimensions.right, params.width)
  }
  if (dragDirections.bottom) {
    updateSnapInfo(snapInfo.bottom, unsnappedDimensions.bottom, params.height)
  }
  if (dragDirections.left) {
    updateSnapInfo(snapInfo.left, unsnappedDimensions.left, params.width)
  }

  const offset = { x: 0, y: 0 }
  // check whether we should prefer to snap to the left or right
  const horizontalCutOff = params.alignmentSnapFactor * params.horizontalResolution
  const optimalHorizontalOffset = minByAbsoluteValue(
    dragDirections.left ? snapInfo.left.currentOffset : horizontalCutOff,
    dragDirections.right ? snapInfo.right.currentOffset : horizontalCutOff,
  )
  offset.x = Math.abs(optimalHorizontalOffset) < horizontalCutOff ? optimalHorizontalOffset : 0

  const verticalCutOff = params.alignmentSnapFactor * params.verticalResolution
  const optimalVerticalOffset = minByAbsoluteValue(
    dragDirections.top ? snapInfo.top.currentOffset : verticalCutOff,
    dragDirections.bottom ? snapInfo.bottom.currentOffset : verticalCutOff,
  )
  offset.y = Math.abs(optimalVerticalOffset) < verticalCutOff ? optimalVerticalOffset : 0

  // check whether we should prefer to snap to the top or bottom
  if (
    !dragDirections.bottom ||
    Math.abs(snapInfo.top.currentOffset) < Math.abs(snapInfo.bottom.currentOffset)
  ) {
    if (
      dragDirections.top &&
      Math.abs(snapInfo.top.currentOffset) < params.alignmentSnapFactor * params.verticalResolution
    ) {
      offset.y = snapInfo.top.currentOffset
    }
  } else if (
    dragDirections.bottom &&
    (!dragDirections.top ||
      Math.abs(snapInfo.bottom.currentOffset) <
        params.alignmentSnapFactor * params.verticalResolution)
  ) {
    offset.y = snapInfo.bottom.currentOffset
  }

  const snappedDimensions = {
    left: unsnappedDimensions.left + (dragDirections.left ? offset.x : 0),
    right: unsnappedDimensions.right + (dragDirections.right ? offset.x : 0),
    top: unsnappedDimensions.top + (dragDirections.top ? offset.y : 0),
    bottom: unsnappedDimensions.bottom + (dragDirections.bottom ? offset.y : 0),
  }

  const snappedPosition = {
    x: snappedDimensions.left,
    y: snappedDimensions.top,
    width: snappedDimensions.right - snappedDimensions.left,
    height: snappedDimensions.bottom - snappedDimensions.top,
  }

  snapInfo.top.indicator.attr("y1", snappedDimensions.top).attr("y2", snappedDimensions.top)
  snapInfo.right.indicator.attr("x1", snappedDimensions.right).attr("x2", snappedDimensions.right)
  snapInfo.bottom.indicator
    .attr("y1", snappedDimensions.bottom)
    .attr("y2", snappedDimensions.bottom)
  snapInfo.left.indicator.attr("x1", snappedDimensions.left).attr("x2", snappedDimensions.left)
  snapInfo.top.indicator.attr(
    "opacity",
    dragDirections.top && offset.y === snapInfo.top.currentOffset ? 1 : 0,
  )
  snapInfo.right.indicator.attr(
    "opacity",
    dragDirections.right && offset.x === snapInfo.right.currentOffset ? 1 : 0,
  )
  snapInfo.bottom.indicator.attr(
    "opacity",
    dragDirections.bottom && offset.y === snapInfo.bottom.currentOffset ? 1 : 0,
  )
  snapInfo.left.indicator.attr(
    "opacity",
    dragDirections.left && offset.x === snapInfo.left.currentOffset ? 1 : 0,
  )

  return snappedPosition
}

export const done = (snapInfo: EdgesSnapInfo) => {
  snapInfo.left.indicator.remove()
  snapInfo.right.indicator.remove()
  snapInfo.top.indicator.remove()
  snapInfo.bottom.indicator.remove()
}
